<template>
    <button
        ref="button"
        class="button"
        :class="`button--${theme} button--size-${size}${disabled ? ' disabled' : ''}`"
        :type="type"
        :style="buttonStyle"
        :disabled="disabled ? 'true' : undefined"
    >
        <app-icon v-if="icon" :name="icon" class="button__icon mr-6" hidden />

        <spinner v-if="isLoading" />
        <slot v-else />
    </button>
</template>

<script>
import AppIcon from '@/components/ui/Icon.vue'
import Spinner from '@/components/ui/Spinner.vue'

const buttonThemes = ['primary', 'default', 'highlight']

export default {
    name: 'Button',

    components: { Spinner, AppIcon },

    props: {
        theme: { type: String, default: 'primary', validator: val => buttonThemes.includes(val) },
        icon: { type: String, default: null },
        size: { type: String, default: 'default', validator: val => ['default', 'small'].includes(val) },
        disabled: { type: Boolean, default: false },
        isLoading: { type: Boolean, default: false },
        type: { type: String, default: 'button' },
    },

    data: () => ({ buttonWidth: 0, buttonHeight: 0 }),

    computed: {
        buttonStyle () {
            if (!this.buttonWidth && !this.buttonHeight) return undefined

            return {
                height: `${this.buttonHeight}px`,
                width: `${this.buttonWidth}px`,
            }
        },
    },

    watch: {
        /**
         * Make sure that button width stays the same while loading
         */
        isLoading () {
            if (this.isLoading) {
                this.buttonHeight = this.$refs.button.offsetHeight || 0
                this.buttonWidth = this.$refs.button.offsetWidth || 0
            } else {
                this.buttonHeight = 0
                this.buttonWidth = 0
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.button {
    border: none;
    border-radius: $default-border-radius;
    font-size: $font-size-14;
    padding: $spacing-unit-6 $spacing-unit-24;
    min-height: $button-min-height;

    &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &--primary {
        background: $primary-color;
        color: $color-text-inverted;

        @include interaction {
            &:not(.disabled) {
                background: lighten($primary-color, 6%);
            }
        }
    }

    &--default {
        color: $color-text-default;
        background: darken($tertiary-color, 6%);

        @include interaction {
            &:not(.disabled) {
                color: $color-text-default;
                background: darken($tertiary-color, 10%);
            }
        }
    }

    &--highlight {
        background: $highlight-color;
        color: $color-text-inverted;

        @include interaction {
            &:not(.disabled) {
                background: lighten($highlight-color, 6%);
            }
        }
    }

    &--size-small {
        min-height: $button-min-height-small;
    }

    &__icon {
        vertical-align: text-bottom; /* Align icon with text */
    }
}
</style>
